import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, Target, Users, X } from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import '../styles/ScamSmart.css';

const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';
const FAMILY_KEY = 'ss_family_profiles';
const ACTIVE_KEY = 'ss_active_profile_id';
const LATEST_KEY = 'ss_latest_result';

const ROLES = ['Me', 'Partner', 'Mum', 'Dad', 'Teen', 'Other'];
const CATEGORY_LABEL = {
  core: 'Core scam patterns',
  family_emergency: 'Family emergency',
  banking_government: 'Banking & gov impersonation',
  delivery_shopping: 'Delivery & shopping',
  marketplace_payid: 'Marketplace / PayID',
};

const ScamSmartFamily = () => {
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('Me');

  // Load on mount + capture latest test result onto active profile, if any
  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAMILY_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const active = localStorage.getItem(ACTIVE_KEY);
      const latestRaw = localStorage.getItem(LATEST_KEY);
      let next = list;
      if (active && latestRaw) {
        const latest = JSON.parse(latestRaw);
        // Has this active profile already absorbed this result?
        next = list.map((p) => {
          if (p.id !== active) return p;
          if (p.lastResultAt === latest.at) return p;
          return {
            ...p,
            score: latest.score,
            rank: latest.rank,
            safe: latest.safe,
            risky: latest.risky,
            dangerous: latest.dangerous,
            strong: latest.strong,
            weak: latest.weak,
            tests: (p.tests || 0) + 1,
            lastResultAt: latest.at,
          };
        });
        try { localStorage.setItem(FAMILY_KEY, JSON.stringify(next)); } catch {}
      }
      setProfiles(next);
      setActiveId(active);
    } catch {}
  }, []);

  const persist = (next) => {
    setProfiles(next);
    try { localStorage.setItem(FAMILY_KEY, JSON.stringify(next)); } catch {}
  };

  const addProfile = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) return;
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    persist([...profiles, { id, name: name.trim(), role, tests: 0 }]);
    setName('');
    setRole('Me');
  };

  const removeProfile = (id) => {
    persist(profiles.filter((p) => p.id !== id));
    if (activeId === id) {
      try { localStorage.removeItem(ACTIVE_KEY); } catch {}
      setActiveId(null);
    }
  };

  const setActive = (id) => {
    try { localStorage.setItem(ACTIVE_KEY, id); } catch {}
    setActiveId(id);
  };

  return (
    <div className="ss-page" data-testid="ss-family-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">Family Scorecard</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container" style={{ maxWidth: 1100 }}>
          <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>

          <div className="ss-section-eyebrow">Family Scorecard</div>
          <h1 className="ss-section-title" style={{ fontSize: 28 }}>Train without lecturing them.</h1>
          <p className="ss-section-lede" style={{ fontSize: 15 }}>
            Add each family member, then mark whoever is taking the next Scam IQ test as <strong>active</strong>.
            Results get logged to their profile so you can see who's confident — and who could use a Sunday afternoon practice.
          </p>

          <form className="ss-add-profile-row" onSubmit={addProfile} data-testid="ss-add-profile-form">
            <input
              type="text"
              placeholder="Name (e.g. Mum, Lucy, Dad)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="ss-add-profile-name"
              maxLength={40}
            />
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="ss-add-profile-role">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="submit" className="ss-btn-primary" disabled={!name.trim()} data-testid="ss-add-profile-submit">
              <UserPlus size={16} /> Add profile
            </button>
          </form>

          {profiles.length === 0 && (
            <div className="ss-empty-state">
              <Users size={32} color="#5C6B73" style={{ margin: '0 auto 10px' }} />
              <p style={{ margin: 0 }}>No family profiles yet. Add yourself first, then add the others.</p>
            </div>
          )}

          {profiles.length > 0 && (
            <div className="ss-family-grid" data-testid="ss-family-grid">
              {profiles.map((p) => {
                const isActive = p.id === activeId;
                return (
                  <div key={p.id} className={`ss-profile-card ${isActive ? 'active' : ''}`} data-testid={`ss-profile-${p.id}`}>
                    <button className="ss-profile-remove" onClick={() => removeProfile(p.id)} aria-label={`Remove ${p.name}`}>
                      <X size={16} />
                    </button>
                    <div className="ss-profile-head">
                      <div className="ss-profile-avatar">{(p.name || '?').slice(0, 2).toUpperCase()}</div>
                      <div>
                        <p className="ss-profile-name">{p.name}</p>
                        <div className="ss-profile-role">{p.role}{isActive ? ' · active' : ''}</div>
                      </div>
                    </div>
                    <div className="ss-profile-stats">
                      <div className="ss-profile-stat">
                        <div className="label">Latest Scam IQ</div>
                        <div className="val ss-profile-iq">{typeof p.score === 'number' ? p.score : '—'}</div>
                      </div>
                      <div className="ss-profile-stat">
                        <div className="label">Rank</div>
                        <div className="val" style={{ fontSize: 12 }}>{p.rank || 'Not tested'}</div>
                      </div>
                      <div className="ss-profile-stat">
                        <div className="label">Strongest</div>
                        <div className="val" style={{ fontSize: 11 }}>{CATEGORY_LABEL[p.strong] || '—'}</div>
                      </div>
                      <div className="ss-profile-stat">
                        <div className="label">Weak spot</div>
                        <div className="val" style={{ fontSize: 11 }}>{CATEGORY_LABEL[p.weak] || '—'}</div>
                      </div>
                    </div>
                    <div className="ss-profile-actions">
                      <button
                        className={`ss-profile-pill-btn ${isActive ? 'primary' : ''}`}
                        onClick={() => setActive(p.id)}
                        data-testid={`ss-profile-activate-${p.id}`}
                      >
                        {isActive ? '✓ Active' : 'Set active'}
                      </button>
                      <Link to="/scamsmart/test" className="ss-profile-pill-btn primary" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <Target size={12} /> Test
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="ss-trusted" style={{ marginTop: 28 }}>
            <div className="ss-trusted-title">How it works</div>
            Pick the family member who's about to take the test → tap <strong>Set active</strong> → hand them the device.
            When they finish the Scam IQ Test, their score lands on this card automatically. Profiles live on this device only —
            no accounts, no sharing.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartFamily;
